import { useMemo, useCallback, useState } from "react";
import { useRouteLoaderData } from "@remix-run/react";

import { useLabels } from "~/hooks/useLabels";
import { useRouteAction } from "~/hooks/useRouteAction";
import { sendMessageToHost } from "~/utils/events";
import { sharePage } from "~/utils/share";
import { pathFor, urlFor } from "~/utils/url";
import { track } from "~/utils/track";
import { showShareButton } from "~/utils/features";
import { SessionCard } from "~/components/sessionCard/SessionCard";
import { RouteType, Action } from "~/constants";
import { ShareModal } from "~/components/shareModal/ShareModal";
import { getArtworkFromData } from "~/utils/data";
import { isMobileNative } from "~/utils/helpers";

import type { SyntheticEvent } from "react";
import type { ActionProp } from "~/components/components";
import type {
  SessionCardProps,
  SessionCardStateAndHelpers,
} from "~/components/sessionCard/SessionCard";
import type { ModuleRouteLoaderData } from "../$class_.modules.$module/route";
import type { TrackEventData } from "~/utils/track";
import type { ClassroomImage, Session } from "~/graphql/types";

export interface ModuleIndexItemProps {
  session: Session;
}

export function ModuleIndexItem({ session }: ModuleIndexItemProps) {
  const data = useRouteLoaderData(
    "routes/$class_.modules.$module",
  ) as ModuleRouteLoaderData;

  const { t } = useLabels();

  const { intent, submitAction, fetcher } = useRouteAction();

  const [isShareOpen, setIsShareOpen] = useState(false);

  const trackingData = useMemo<TrackEventData>(
    () => ({
      className: data.module.class?.slug,
      moduleNumber: data.module.position,
      sessionNumber: session.position,
      trigger: "moduleDetailSessionCard",
    }),
    [data.module.class?.slug, data.module.position, session.position],
  );

  const handleShareClose = useCallback(() => {
    setIsShareOpen(false);
  }, []);

  const shareUrl = urlFor(RouteType.SESSION, {
    class: data.module.class?.slug!,
    session: String(session.position),
  });

  const sessionProps = useMemo<SessionCardProps>(() => {
    const artwork = getArtworkFromData(
      data.module?.artwork as ClassroomImage[],
      "1:1",
    )?.url;

    const href = pathFor(RouteType.SESSION, {
      class: data.module.class?.slug!,
      session: String(session.position),
    });

    const isMutating = fetcher.formData?.get("sessionId") === session.id;

    const isCompleteOptimistic = isMutating
      ? intent === Action.MarkSessionComplete
      : Boolean(session.user?.isComplete);

    const progress = isCompleteOptimistic ? 1 : (session.user?.progress ?? 0);

    const renderActions = ({
      closeActions,
    }: SessionCardStateAndHelpers): ActionProp[] => [
      ...(showShareButton()
        ? [
            {
              icon: "arrow-up-from-square",
              text: t("common.cta.share"),
              onClick: (e: SyntheticEvent) => {
                closeActions();

                if (isMobileNative()) {
                  sharePage(shareUrl, e.target);
                } else {
                  setIsShareOpen(true);
                }

                track("crSessionShare", trackingData);
              },
            },
          ]
        : []),
      {
        icon: "circle-check",
        iconVariant: isCompleteOptimistic ? "solid" : "regular",
        text: isCompleteOptimistic
          ? t("common.cta.unmarkSessionComplete")
          : t("common.cta.markSessionComplete"),
        onClick: (e: SyntheticEvent) => {
          submitAction(
            {
              intent: isCompleteOptimistic
                ? Action.UnmarkSessionComplete
                : Action.MarkSessionComplete,
              sessionId: session.id,
            },
            { authRequired: true },
          );
          sendMessageToHost({
            ui: {
              haptic: {
                type: isCompleteOptimistic
                  ? "notificationError"
                  : "notificationSuccess",
              },
            },
          });
        },
      },
    ];

    return {
      renderActions,
      artwork,
      description: session.description,
      duration: session.duration,
      href,
      meta: [
        t("card.session.meta.position", {
          number: session.position,
        }),
      ],
      progress,
      title: session.title,
      noteCount: session.user?.notes?.totalCount,
      onClick() {
        track("crSessionStart", trackingData);
      },
    };
  }, [
    data.module?.artwork,
    data.module.class?.slug,
    fetcher.formData,
    intent,
    session.description,
    session.duration,
    session.id,
    session.position,
    session.title,
    session.user?.isComplete,
    session.user?.notes?.totalCount,
    session.user?.progress,
    shareUrl,
    submitAction,
    t,
    trackingData,
  ]);

  return (
    <>
      <SessionCard {...sessionProps} />
      <ShareModal
        isOpen={isShareOpen}
        shareUrl={shareUrl}
        onRequestClose={handleShareClose}
      />
    </>
  );
}
